import React, { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'

import { useTranslations } from 'next-intl'

import {
  Text,
  Link,
  Box,
  useColorModeValue,
  useBreakpointValue,
  Button,
  Modal,
  ModalHeader,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalCloseButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Select,
  VStack,
  Input,
  Switch,
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertDescription,
  Checkbox,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  Image,
  Divider,
  Tooltip,
} from '@chakra-ui/react'

import { TriangleDownIcon, InfoOutlineIcon } from '@chakra-ui/icons'

import {
  getAllMiningPools,
  addPoolAccountMutation,
  getUserMiningPools,
  updateMiningPoolsAccountPerMiner,
} from '@/api/miningPool'

import { IMiningPoolNode } from '@/types/miningPool.interface'

import { Controller, useForm } from 'react-hook-form'
import {
  ALGORITHMS,
  ENABLE_POOL_PER_MINER_UI,
  SUPPORT_MINING_POOL_CONFIGURATION_URL,
  SUPPORT_SPIDER_POOL_CONFIGURATION_URL,
} from '@/constants'
import { RightArrowIcon } from '@/components/icons/RightArrowIcon'
import { NOT_XSS_PATTERN } from '@/utils/inputPattern'
import { setMiningPoolImage } from '@/layouts/DashboardLayout/constants'

type MiningPoolFormValues = {
  miningPoolId: string
  username: string
  watcherLink: string
  nickName: string
  miningPoolAgreement: boolean
  defaultMiningPool: boolean
  miningPoolStratum: string
  backupStratum1: string
  backupStratum2: string
  algorithm: string
  updateAllMiners: boolean
}

type MiningPoolOption = {
  value: string
  label: string
  icon: string
  isPartneredPool: boolean
  discountedPoolFee: string
}

type AddPoolAccountModalProps = {
  isOpen: boolean
  onClose: () => void
  userMiningPools: { userMiningPools: { nodes: IMiningPoolNode[] } }
}

const AddPoolAccountModal: React.FC<AddPoolAccountModalProps> = ({
  isOpen,
  onClose,
  userMiningPools,
}: AddPoolAccountModalProps) => {
  const t = useTranslations()
  const toast = useToast()
  const blue50 = useColorModeValue('blue.50', 'blue.900')
  const gray700 = useColorModeValue('gray.700', 'gray.200')
  const gray800 = useColorModeValue('gray.800', 'gray.200')
  const gray870 = useColorModeValue('gray.870', 'gray.300')
  const gray500 = useColorModeValue('gray.500', 'gray.400')
  const gray50 = useColorModeValue('gray.50', 'black.100')
  const gray600 = useColorModeValue('gray.200', 'black.200')
  const modalBg = useColorModeValue('white', 'black.400')
  const inputBg = useColorModeValue('white', 'black.100')
  const inputBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200')

  const [isUsernameTooltipOpen, setIsUsernameTooltipOpen] = useState(false)
  const usernameTooltipTimer = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const [defaultMiningPool, setDefaultMiningPool] = useState(false)
  const [selectedMiningPoolName, setSelectedMiningPoolName] =
    useState<string>('')
  const [isUpdateAllMinersChecked, setIsUpdateAllMinersChecked] =
    useState(false)
  const [spiderFeeLoading, setSpiderFeeLoading] = useState(false)
  const [spiderFeeError, setSpiderFeeError] = useState<string | null>(null)

  const { data, loading } = useQuery(getAllMiningPools)

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUpdateAllMinersChecked(e.target.checked)
  }

  useEffect(() => {
    if (!isOpen) {
      setIsUpdateAllMinersChecked(false)
      setSpiderFeeLoading(false)
      setSpiderFeeError(null)
    }
  }, [isOpen])

  const isSpiderPool = selectedMiningPoolName.toLowerCase() === 'spider pool'
  const usernameRef = React.useRef<string>('')

  const [
    addPoolAccount,
    {
      loading: addPoolAccountLoading,
      error: addPoolAccountError,
      reset: resetAddPoolAccountErrors,
    },
  ] = useMutation(addPoolAccountMutation, {
    onCompleted(data) {
      if (data?.addPoolAccount !== null) {
        const usesCompassPrefix = usernameRef.current
          .toLowerCase()
          .startsWith('compass')
        if (isSpiderPool && usesCompassPrefix) {
          toast({
            title: t('dashboard.mining-pool.add-pool-toast-success'),
            description: t(
              'dashboard.mining-pool.add-pool-spider-toast-success-description'
            ),
            status: 'success',
            position: 'top',
          })
        } else {
          toast({
            title: t('dashboard.mining-pool.add-pool-toast-success'),
            description: t(
              'dashboard.mining-pool.add-pool-toast-success-description'
            ),
            status: 'success',
            position: 'top',
          })
        }
        reset()
        onClose()
        setDefaultMiningPool(false)
        setIsUpdateAllMinersChecked(false)
      }
    },
    refetchQueries: [
      {
        query: getUserMiningPools,
      },
    ],
  })

  const [updateAllMiningPoolsAccountPerMiner] = useMutation(
    updateMiningPoolsAccountPerMiner,
    {
      onCompleted(data) {
        if (data?.addPoolAccount !== null) {
          const usesCompassPrefix = usernameRef.current
            .toLowerCase()
            .startsWith('compass')
          if (isSpiderPool && usesCompassPrefix) {
            toast({
              title: t('dashboard.mining-pool.add-pool-toast-success'),
              description: t(
                'dashboard.mining-pool.add-pool-spider-toast-success-description'
              ),
              status: 'success',
              position: 'top',
            })
          } else {
            toast({
              title: t('dashboard.mining-pool.add-pool-toast-success'),
              description: t(
                'dashboard.mining-pool.add-pool-toast-success-description'
              ),
              status: 'success',
            })
          }
          reset()
          onClose()
          setDefaultMiningPool(false)
          setIsUpdateAllMinersChecked(false)
        }
      },
      onError(error) {
        throw new Error(error.message)
      },
      refetchQueries: [
        {
          query: getUserMiningPools,
        },
      ],
    }
  )

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    clearErrors,
    watch,
    control,
    setValue,
  } = useForm<MiningPoolFormValues>()

  const selectedAlgorithm = watch('algorithm')?.toUpperCase()

  const algorithmValues: Record<string, string> = { BLAKE2S: 'BLAKE2_S' }

  // Returns true if the user already has a default pool for the given algorithm
  const doesDefaultMiningPoolExistOnUser = (algorithm: string): boolean => {
    if (!algorithm) return false
    return (
      userMiningPools?.userMiningPools?.nodes?.some(
        (pool: IMiningPoolNode) =>
          pool.miningPoolAccountDefaultPool &&
          pool.miningPoolAlgorithm.toUpperCase() === algorithm.toUpperCase()
      ) ?? false
    )
  }

  async function onSubmit(values: MiningPoolFormValues) {
    setSpiderFeeError(null)
    usernameRef.current = values.username ?? ''

    const miningPoolAccount = await addPoolAccount({
      variables: {
        algorithm: values?.algorithm?.toUpperCase(),
        miningPoolsAccountsDefaultPool: doesDefaultMiningPoolExistOnUser(
          selectedAlgorithm
        )
          ? defaultMiningPool
          : true,
        miningPoolId:
          selectedMiningPoolName === 'Other Pool' ? '' : values.miningPoolId,
        nickName: '',
        poolName: selectedMiningPoolName,
        userName: values.username,
        watcherUri: values.watcherLink,
        stratum1: values.miningPoolStratum || '',
        stratum2: values.backupStratum1 || '',
        stratum3: values.backupStratum2 || '',
      },
    })

    if (isSpiderPool && miningPoolAccount?.errors?.length) {
      setSpiderFeeError(
        miningPoolAccount.errors[0].message ??
          'An error occurred. Please try again.'
      )
      return
    }

    const miningPoolAccountId = miningPoolAccount?.data?.addPoolAccount?.uuid

    if (!miningPoolAccountId) return

    // Update all miners if checked
    if (isUpdateAllMinersChecked) {
      updateAllMiningPoolsAccountPerMiner({
        variables: {
          miningPoolsAccountsId: miningPoolAccountId,
          algorithm: values.algorithm,
        },
      })
    }

    reset()
    onClose()
    setDefaultMiningPool(false)
  }

  const options: MiningPoolOption[] = React.useMemo(() => {
    const poolOptions =
      data?.getMiningPoolsList?.nodes
        ?.filter(
          (pool: IMiningPoolNode) =>
            pool.display &&
            !pool.isPartneredPool &&
            pool.miningPoolAlgorithm === selectedAlgorithm &&
            pool.miningPoolName !== 'Other Pool'
        )
        .map((pool: IMiningPoolNode) => ({
          value: pool.miningPoolId,
          label: pool.miningPoolName,
          icon: pool.miningPoolIcon,
        })) || []
    return poolOptions
  }, [data, selectedAlgorithm])

  const partneredPoolOptions: MiningPoolOption[] = React.useMemo(() => {
    const poolOptions =
      data?.getMiningPoolsList?.nodes
        ?.filter(
          (pool: IMiningPoolNode) =>
            pool.display &&
            pool.isPartneredPool &&
            pool.miningPoolAlgorithm === selectedAlgorithm &&
            pool.miningPoolName !== 'Other Pool'
        )
        .map((pool: IMiningPoolNode) => ({
          value: pool.miningPoolId,
          label: pool.miningPoolName,
          icon: pool.miningPoolIcon,
          discountedPoolFee: pool.discountedPoolFee,
          isPartneredPool: pool.isPartneredPool,
        })) || []
    return poolOptions
  }, [data, selectedAlgorithm])

  const isMiningPoolDisabled = !selectedAlgorithm
  const selectedValue = watch('miningPoolId')
  const selectedOption = React.useMemo(() => {
    return (
      partneredPoolOptions.find((opt) => opt.value === selectedValue) ||
      options.find((opt) => opt.value === selectedValue)
    )
  }, [selectedValue, partneredPoolOptions, options])

  useEffect(() => {
    selectedOption && setSelectedMiningPoolName(selectedOption?.label ?? null)
  }, [selectedOption])

  const isMobile = useBreakpointValue({ base: true, md: false })

  const handleClose = () => {
    onClose()
    reset()
    clearErrors()
    setSelectedMiningPoolName('')
    resetAddPoolAccountErrors()
    doesDefaultMiningPoolExistOnUser(selectedAlgorithm)
      ? setDefaultMiningPool(false)
      : null
  }

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={5}>
        {/* Algorithm Dropdown */}
        <FormControl id="algorithm" isInvalid={!!errors?.algorithm} w="full">
          <FormLabel fontSize="sm" color={gray800} fontWeight={600}>
            {t('common.labels.algorithm')}
            <Text as="span" color="orange.500" ml="1">
              *
            </Text>
          </FormLabel>
          <Select
            {...register('algorithm', {
              required: t('forms.errors.is-required'),
            })}
            placeholder={t(
              'dashboard.verification.forms.placeholders.select-option'
            )}
            bg={gray50}
            borderColor={inputBorderColor}
            w="full"
            cursor={'pointer'}
            icon={<TriangleDownIcon boxSize={'16px'} />}
            iconSize="16px"
            iconColor={gray500}
            _hover={{
              bg: gray600,
              borderColor: inputBorderColor,
            }}
          >
            {ALGORITHMS.map((algorithm, index) => {
              const upper = algorithm.toUpperCase()
              const value = algorithmValues[upper] ?? upper

              return (
                <option key={index} value={value}>
                  {algorithm}
                </option>
              )
            })}
          </Select>
          <FormErrorMessage w="full" justifyContent="center">
            <Box textAlign="center">
              {errors.algorithm && errors.algorithm.message}
            </Box>
          </FormErrorMessage>
        </FormControl>
        {/* Mining Pool Dropdown */}
        <FormControl
          id="miningPoolId"
          isInvalid={!!errors.miningPoolId || !!errors.algorithm}
        >
          <FormLabel fontSize="sm" color={gray800} fontWeight={600} mb="1">
            {t('dashboard.mining-pool.mining-pool')}
            <Text as="span" color="orange.500" ml="1">
              *
            </Text>
          </FormLabel>
          <Controller
            name="miningPoolId"
            control={control}
            rules={{
              required: t('forms.errors.is-required'),
            }}
            render={({ field, fieldState }) => (
              <Box
                w="full"
                bg={gray50}
                rounded="md"
                borderWidth="1px"
                borderColor={fieldState.error ? 'red.500' : inputBorderColor}
              >
                <Menu matchWidth>
                  <MenuButton
                    as={Button}
                    width="100%"
                    variant="unstyled"
                    textAlign="left"
                    fontWeight="normal"
                    px="3"
                    color={gray500}
                    isDisabled={isMiningPoolDisabled}
                  >
                    <Flex align="center">
                      {field.value === 'Other Pool' ? (
                        <>
                          <Image
                            src={setMiningPoolImage('Other Pool')}
                            alt={`Other Pool default logo`}
                            boxSize="20px"
                            objectFit="contain"
                            mr={2}
                          />
                          <Text color={gray800}>
                            {t('dashboard.mining-pool.other-pool')}
                          </Text>
                        </>
                      ) : selectedOption ? (
                        <>
                          <Image
                            src={setMiningPoolImage(selectedOption.label)}
                            alt={`${selectedOption.label} logo`}
                            boxSize="20px"
                            objectFit="contain"
                            mr={2}
                          />
                          <Text color={gray800}>{selectedOption.label}</Text>
                          {selectedOption.discountedPoolFee && (
                            <Box
                              flexShrink={0}
                              bg="brand.transparent10"
                              color="brand.600"
                              fontSize="11px"
                              fontWeight="600"
                              px="2"
                              py="0.5"
                              borderRadius="full"
                              whiteSpace="nowrap"
                              ml={2}
                            >
                              {t('dashboard.mining-pool.discount')}
                            </Box>
                          )}
                        </>
                      ) : (
                        <>
                          <svg
                            width="25"
                            height="25"
                            viewBox="0 0 33 33"
                            fill="none"
                            aria-hidden="true"
                          >
                            <circle
                              cx="16.5"
                              cy="16.6914"
                              r="16"
                              fill="#C4C4C4"
                            />
                          </svg>
                          <Text ml={'2'} color={gray800}>
                            {t('dashboard.mining-pool.select-pool')}
                          </Text>
                        </>
                      )}
                    </Flex>
                    <Box
                      as={TriangleDownIcon}
                      boxSize="16px"
                      position="absolute"
                      right="3"
                      top="50%"
                      transform="translateY(-50%)"
                      color="currentColor"
                      pointerEvents="none"
                    />
                  </MenuButton>
                  <MenuList
                    zIndex="popover"
                    maxH="450px"
                    overflowY="auto"
                    borderColor={inputBorderColor}
                    sx={{
                      // Scrollbar styling
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent', // make track invisible
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'gray.300',
                        borderRadius: '10px',
                        border: '2px solid transparent', // creates padding
                        backgroundClip: 'content-box', // ensures border creates padding
                      },
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: 'gray.400',
                        border: '2px solid transparent',
                        backgroundClip: 'content-box',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <VStack spacing={2} px="4" py="2">
                      {partneredPoolOptions.length > 0 && (
                        <>
                          <Text
                            fontSize="11px"
                            fontWeight="600"
                            letterSpacing="wider"
                            textTransform="uppercase"
                            color={gray500}
                            display={'flex'}
                            width={'full'}
                            px="2"
                            pt="1"
                          >
                            {t('dashboard.mining-pool.partner-pools')}
                          </Text>
                          <Divider />
                          {partneredPoolOptions.map((option) => (
                            <MenuItem
                              // bg="primaryElevation"
                              key={option.value}
                              onClick={() => {
                                field.onChange(option.value)
                                clearErrors('miningPoolId')
                              }}
                            >
                              <Flex
                                align="center"
                                justify="space-between"
                                w="full"
                                gap="2"
                              >
                                <Flex align="center" gap="2">
                                  <Image
                                    src={setMiningPoolImage(option.label)}
                                    alt={`${option.label} logo`}
                                    boxSize="20px"
                                    objectFit="contain"
                                    flexShrink={0}
                                  />
                                  <Text color={gray800}>{option.label}</Text>
                                </Flex>
                                {isMobile ? (
                                  <Box
                                    flexShrink={0}
                                    bg="brand.transparent10"
                                    color="brand.600"
                                    fontSize="11px"
                                    fontWeight="400"
                                    px="2"
                                    py="0.5"
                                    borderRadius="full"
                                    whiteSpace="nowrap"
                                  >
                                    {t(
                                      'dashboard.mining-pool.discount-available'
                                    )}
                                  </Box>
                                ) : (
                                  <Text
                                    flexShrink={0}
                                    color={gray500}
                                    fontSize="sm"
                                    whiteSpace="nowrap"
                                  >
                                    {t(
                                      'dashboard.mining-pool.discount-available'
                                    )}
                                  </Text>
                                )}
                              </Flex>
                            </MenuItem>
                          ))}
                        </>
                      )}
                      <Text
                        fontSize="11px"
                        fontWeight="600"
                        letterSpacing="wider"
                        textTransform="uppercase"
                        color={gray500}
                        display={'flex'}
                        width={'full'}
                        px="2"
                        pt="1"
                      >
                        {t('dashboard.mining-pool.other-pools')}
                      </Text>
                      <Divider />
                      {options.map((option) => (
                        <MenuItem
                          key={option.value}
                          onClick={() => {
                            field.onChange(option.value)
                            clearErrors('miningPoolId')
                          }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                      <MenuItem
                        onClick={() => {
                          field.onChange('Other Pool')
                          setSelectedMiningPoolName('Other Pool')
                          clearErrors('miningPoolId')
                        }}
                      >
                        <Text
                          fontSize="16px"
                          color={gray800}
                          justifyContent={'start'}
                          display={'flex'}
                          align={'left'}
                          width={'full'}
                        >
                          {t('dashboard.mining-pool.add-your-own-pool')}
                        </Text>
                      </MenuItem>
                    </VStack>
                  </MenuList>
                </Menu>
                <FormErrorMessage w="full" justifyContent="center">
                  <Box textAlign="center">
                    {isMiningPoolDisabled
                      ? t('dashboard.mining-pool.please-select-algorithm')
                      : fieldState.error?.message}
                  </Box>
                </FormErrorMessage>
              </Box>
            )}
          />
        </FormControl>

        {selectedMiningPoolName === 'Other Pool' && (
          <>
            {/* Mining Pool Stratum */}
            <FormControl
              id="miningPoolStratum"
              isInvalid={!!errors?.miningPoolStratum}
            >
              <FormLabel fontSize="sm" color={gray800}>
                {t('dashboard.mining-pool.mining-pool-stratum')}
                <Text as="span" color="orange.500">
                  *
                </Text>
              </FormLabel>
              <Input
                type="text"
                bg={inputBg}
                borderColor={inputBorderColor}
                _hover={{ borderColor: inputBorderColor }}
                {...register('miningPoolStratum', {
                  required: t('forms.errors.is-required'),
                })}
              />
              <Text
                fontSize="10"
                color={gray800}
                justifyContent={'start'}
                display={'flex'}
                align={'left'}
                width={'full'}
              >
                {t('dashboard.mining-pool.mining-pool-stratum-description')}
                <Link href={SUPPORT_MINING_POOL_CONFIGURATION_URL} isExternal>
                  <Text as="span" mx="2">
                    {t('home.read-more')}
                  </Text>
                </Link>
              </Text>

              <FormErrorMessage w="full" justifyContent="center">
                <Box textAlign="center">
                  {errors.miningPoolStratum && errors.miningPoolStratum.message}
                </Box>
              </FormErrorMessage>
            </FormControl>

            {/* Mining Pool Backup Stratum 1 */}
            <FormControl
              id="backupStratum1"
              isInvalid={!!errors?.backupStratum1}
            >
              <FormLabel fontSize="sm" color={gray800}>
                {t('dashboard.mining-pool.backup-stratum')}
                <Text as="span" color="gray.400" mx="1">
                  ({t('contact-sales.form.optional')})
                </Text>
              </FormLabel>
              <Input
                type="text"
                bg={inputBg}
                borderColor={inputBorderColor}
                _hover={{ borderColor: inputBorderColor }}
                {...register('backupStratum1')}
              />
              <FormErrorMessage w="full" justifyContent="center">
                <Box textAlign="center">
                  {errors.backupStratum1 && errors.backupStratum1.message}
                </Box>
              </FormErrorMessage>
            </FormControl>

            {/* Mining Pool Backup Stratum 2 */}
            <FormControl
              id="backupStratum2"
              isInvalid={!!errors?.backupStratum2}
            >
              <FormLabel fontSize="sm" color={gray800}>
                {t('dashboard.mining-pool.backup-stratum')}
                <Text as="span" color="gray.400" mx="1">
                  ({t('contact-sales.form.optional')})
                </Text>
              </FormLabel>
              <Input
                type="text"
                bg={inputBg}
                borderColor={inputBorderColor}
                _hover={{ borderColor: inputBorderColor }}
                {...register('backupStratum2')}
              />
              <FormErrorMessage w="full" justifyContent="center">
                <Box textAlign="center">
                  {errors.backupStratum2 && errors.backupStratum2.message}
                </Box>
              </FormErrorMessage>
            </FormControl>
          </>
        )}

        {/* Username */}
        <FormControl
          id="username"
          isInvalid={!!errors?.username || (isSpiderPool && !!spiderFeeError)}
        >
          <Flex align="center" justify="space-between" w="full" mb="1" gap="1">
            <Flex align="center" gap="2" flexShrink={0}>
              <FormLabel
                fontSize="sm"
                color={gray800}
                mb="0"
                whiteSpace="nowrap"
                fontWeight={600}
              >
                {isSpiderPool
                  ? 'Spider Pool Account'
                  : t('dashboard.mining-pool.username')}
                <Text as="span" color="orange.500" ml="1">
                  *
                </Text>
              </FormLabel>
              <Box
                position="relative"
                display="inline-flex"
                alignItems="center"
                onMouseEnter={() => {
                  if (usernameTooltipTimer.current)
                    clearTimeout(usernameTooltipTimer.current)
                  setIsUsernameTooltipOpen(true)
                }}
                onMouseLeave={() => {
                  usernameTooltipTimer.current = setTimeout(
                    () => setIsUsernameTooltipOpen(false),
                    100
                  )
                }}
              >
                <InfoOutlineIcon
                  boxSize="12px"
                  color={gray500}
                  cursor="pointer"
                />
                {isUsernameTooltipOpen && (
                  <Box
                    position="absolute"
                    bottom="calc(100% + 8px)"
                    left="50%"
                    transform="translateX(-50%)"
                    maxW="320px"
                    w="max-content"
                    bg="gray.700"
                    color="white"
                    fontSize="sm"
                    rounded="md"
                    px="3"
                    py="2"
                    whiteSpace="normal"
                    zIndex={10020}
                    onMouseEnter={() => {
                      if (usernameTooltipTimer.current)
                        clearTimeout(usernameTooltipTimer.current)
                    }}
                    onMouseLeave={() => {
                      usernameTooltipTimer.current = setTimeout(
                        () => setIsUsernameTooltipOpen(false),
                        100
                      )
                    }}
                    _after={{
                      content: '""',
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      borderWidth: '6px',
                      borderStyle: 'solid',
                      borderColor:
                        'var(--chakra-colors-gray-700) transparent transparent transparent',
                    }}
                  >
                    {t('dashboard.mining-pool.username-description')}{' '}
                    <Link
                      href={SUPPORT_MINING_POOL_CONFIGURATION_URL}
                      isExternal
                      textDecoration="underline"
                      color="white"
                    >
                      {t('dashboard.mining-pool.tooltip-username-learn-more')}
                    </Link>
                  </Box>
                )}
              </Box>
            </Flex>
            <Text
              fontSize="xs"
              color={gray500}
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {t('dashboard.mining-pool.case-sensitive')}
            </Text>
          </Flex>
          <Input
            type="text"
            bg={inputBg}
            borderColor={
              isSpiderPool && spiderFeeError ? 'red.500' : inputBorderColor
            }
            _hover={
              isSpiderPool && spiderFeeError
                ? { borderColor: 'red.500' }
                : { borderColor: inputBorderColor }
            }
            placeholder={t(
              'dashboard.verification.forms.account-name-form.label-input'
            )}
            {...register('username', {
              required: t('forms.errors.is-required'),
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value
                const formattedValue = isSpiderPool
                  ? value.toLowerCase()
                  : value
                setValue('username', formattedValue, { shouldValidate: true })
                if (spiderFeeError) setSpiderFeeError(null)
              },
            })}
          />

          {isSpiderPool && spiderFeeError ? (
            <Text fontSize="sm" color="red.500" mt="1">
              {spiderFeeError}
            </Text>
          ) : (
            <FormErrorMessage>
              {errors.username && errors.username.message}
            </FormErrorMessage>
          )}
          {isSpiderPool && (
            <>
              <Box
                bg={blue50}
                p="0 8px"
                justifyContent="center"
                rounded={'sm'}
                mt="2"
              >
                <HStack align="center" spacing="2">
                  <Text color={gray700} fontSize="xs" fontWeight={400}>
                    {t('dashboard.mining-pool.spider-pool-username-prefix')}
                  </Text>
                  <Link
                    href={SUPPORT_SPIDER_POOL_CONFIGURATION_URL}
                    isExternal
                    textDecoration="underline"
                    color="blue.500"
                    fontSize="xs"
                    fontWeight={600}
                    _hover={{ color: 'blue.600' }}
                  >
                    <Text>
                      {t('dashboard.mining-pool.tooltip-username-learn-more')}
                    </Text>
                  </Link>
                </HStack>
              </Box>
            </>
          )}
        </FormControl>

        {/* Watcher Link */}
        <FormControl id="watcherLink" isInvalid={!!errors?.watcherLink}>
          <Flex align="center" gap="2" mb="1">
            <Text fontSize="sm" color={gray800} fontWeight={600}>
              {t('dashboard.mining-pool.watcher-link')}
            </Text>
            <Tooltip
              hasArrow
              placement="top"
              rounded="md"
              px="3"
              py="2"
              label={t('dashboard.mining-pool.tooltip-watcher-link')}
            >
              <Box display="inline-flex" alignItems="center">
                <InfoOutlineIcon
                  boxSize="12px"
                  color={gray500}
                  cursor="pointer"
                />
              </Box>
            </Tooltip>
          </Flex>
          <Input
            type="text"
            bg={inputBg}
            borderColor={inputBorderColor}
            _hover={{ borderColor: inputBorderColor }}
            placeholder="Watcher link"
            {...register('watcherLink', {
              pattern: {
                value: NOT_XSS_PATTERN,
                message: t(
                  'dashboard.settings.user-account-settings.errors.invalid-pattern',
                  { field: t('dashboard.mining-pool.watcher-link') }
                ),
              },
            })}
          />

          <FormErrorMessage w="full" justifyContent="center">
            <Box textAlign="center">
              {errors.watcherLink && errors.watcherLink.message}
            </Box>
          </FormErrorMessage>
        </FormControl>

        {/* Default Mining Pool Button */}
        <VStack spacing={3} mt="2">
          <FormControl
            id="defaultMiningPool"
            isInvalid={!!errors?.defaultMiningPool}
          >
            <HStack w="full" justifyContent={'flex-start'}>
              <Text fontSize={'sm'} color={gray800} fontWeight={600}>
                {t('dashboard.mining-pool.set-as-default')}
              </Text>
              <Switch
                colorScheme="brand"
                variant={'solid'}
                size="sm"
                {...register('defaultMiningPool')}
                isDisabled={
                  !doesDefaultMiningPoolExistOnUser(selectedAlgorithm)
                }
                isChecked={
                  !doesDefaultMiningPoolExistOnUser(selectedAlgorithm)
                    ? true
                    : undefined
                }
                onChange={(e) => {
                  const { checked } = e.target
                  if (checked) {
                    setDefaultMiningPool(true)
                  } else {
                    setDefaultMiningPool(false)
                  }
                }}
              />
            </HStack>
            <FormErrorMessage>
              {errors.defaultMiningPool && errors.defaultMiningPool.message}
            </FormErrorMessage>
          </FormControl>

          {/* Mining Pool Agreement */}
          <FormControl
            id="miningPoolAgreement"
            isInvalid={!!errors?.miningPoolAgreement}
          >
            <Checkbox
              colorScheme="brand"
              border={gray800}
              variant={'solid'}
              size="lg"
              mt="2"
              {...register('miningPoolAgreement', {
                required: t('forms.errors.is-required'),
                minLength: {
                  value: 4,
                  message: t('forms.errors.min-length', { length: 4 }),
                },
              })}
            >
              <Text color={gray870} fontSize={'10px'} pb={3}>
                {t('dashboard.mining-pool.mining-pool-agreement')}
              </Text>
            </Checkbox>

            <FormErrorMessage w="full" justifyContent="center" mt="-2" mb="2">
              <Box textAlign="center">
                {errors.miningPoolAgreement &&
                  errors.miningPoolAgreement.message}
              </Box>
            </FormErrorMessage>
          </FormControl>
        </VStack>

        {/* Update all Miners checkbox */}
        {ENABLE_POOL_PER_MINER_UI === 'true' && (
          <FormControl
            id="updateAllMiners"
            isInvalid={!!errors?.updateAllMiners}
          >
            <HStack w="full" justifyContent={'center'}>
              <Checkbox
                colorScheme="brand"
                variant={'solid'}
                size="md"
                {...register('updateAllMiners')}
                alignSelf={'center'}
                isChecked={isUpdateAllMinersChecked}
                onChange={handleCheckboxChange}
              >
                <Text color={gray800} fontSize={'11px'} align={'center'}>
                  {t('dashboard.mining-pool.update-all-miners')}
                </Text>
              </Checkbox>
              <FormErrorMessage>
                {errors.updateAllMiners && errors.updateAllMiners.message}
              </FormErrorMessage>
            </HStack>
          </FormControl>
        )}

        {addPoolAccountError && !isSpiderPool && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>{addPoolAccountError.message}</AlertDescription>
          </Alert>
        )}

        <Text fontSize={'sm'} color={gray800} fontFamily={'Inter'}>
          {t('dashboard.mining-pool.need-help-setting-up')}
          <Link href={SUPPORT_MINING_POOL_CONFIGURATION_URL} isExternal>
            <Text
              as="span"
              color={'brand.600'}
              ml="2"
              _hover={{
                color: 'brand.500',
              }}
            >
              {t('dashboard.mining-pool.view-our-guide')}
              <RightArrowIcon ml={2} boxSize={'12px'} />
            </Text>
          </Link>
        </Text>
      </VStack>
      <Divider my="5" />

      {/* Submit + Cancel Buttons */}
      <HStack w="100%" spacing={3} mt="2">
        <Button
          variant="outline"
          colorScheme="gray"
          flex="1"
          onClick={handleClose}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="brand"
          colorScheme="brand"
          flex="1"
          isLoading={loading || addPoolAccountLoading || spiderFeeLoading}
          disabled={
            loading ||
            addPoolAccountLoading ||
            spiderFeeLoading ||
            Object.keys(errors).length > 0
          }
        >
          {t('dashboard.mining-pool.add-pool')}
        </Button>
      </HStack>
    </form>
  )

  return (
    <>
      {isMobile ? (
        <Drawer isOpen={isOpen} placement="bottom" onClose={handleClose}>
          <DrawerOverlay />
          <DrawerContent
            bg={modalBg}
            borderTopRadius="xl"
            maxH="95vh"
            overflowY="auto"
          >
            <DrawerHeader
              alignItems="flex-start"
              justifyContent="flex-start"
              display="flex"
              pt="6"
              pb="1"
              borderBottomWidth="1px"
            >
              {t('dashboard.mining-pool.add-pool-account')}
            </DrawerHeader>
            <DrawerCloseButton />
            <DrawerBody pt="5" position="relative">
              {formContent}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      ) : (
        <Modal
          isCentered
          onClose={handleClose}
          closeOnOverlayClick={true}
          isOpen={isOpen}
          motionPreset="slideInBottom"
        >
          <ModalOverlay />
          <ModalContent maxW="626px" bg={modalBg}>
            <ModalHeader
              alignItems={'flex-start'}
              justifyContent={'flex-start'}
              display={'flex'}
              pt="6"
              pb="1"
            >
              {t('dashboard.mining-pool.add-pool-account')}
            </ModalHeader>
            <Divider mt="3" />
            <ModalCloseButton />
            <ModalBody pt="5" position="relative">
              {formContent}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  )
}

export default AddPoolAccountModal
